import React from 'react';

import { OrgUnit } from '../types';

import AddUnitModal from './AddUnitModal';
import EditUnitModal from './EditUnitModal';
import MoveUnitModal from './MoveUnitModal';
import DeleteUnitModal from './DeleteUnitModal';

/**
 * The last value that was set. A dialog keeps showing it while it animates closed, after the
 * page has cleared its state, so it does not flash "Unit" or an empty name on the way out.
 */
const useLastValue = <T,>(value: T | null): T | null => {
  const [last, setLast] = React.useState<T | null>(value);

  React.useEffect(() => {
    if (value !== null) setLast(value);
  }, [value]);

  return value ?? last;
};

export interface UnitDialogsProps {
  units: OrgUnit[];
  topId: string;
  expandedIds: Set<string>;
  /** The unit each dialog acts on, or null when that dialog is closed. */
  addingUnder: string | null;
  editing: string | null;
  moving: string | null;
  deleting: string | null;
  onCancelAdd: () => void;
  onCancelEdit: () => void;
  onCancelMove: () => void;
  onCancelDelete: () => void;
  onAdd: (name: string, parentId: string) => Promise<void>;
  onEdit: (name: string) => Promise<void>;
  onMove: (newParentId: string) => Promise<void>;
  onDelete: (destinationId: string) => Promise<void>;
}

const UnitDialogs: React.FunctionComponent<UnitDialogsProps> = ({
  units,
  topId,
  expandedIds,
  addingUnder,
  editing,
  moving,
  deleting,
  onCancelAdd,
  onCancelEdit,
  onCancelMove,
  onCancelDelete,
  onAdd,
  onEdit,
  onMove,
  onDelete,
}) => {
  const shownAddingUnder = useLastValue(addingUnder);
  const shownEditing = useLastValue(editing);
  const shownMoving = useLastValue(moving);
  const shownDeleting = useLastValue(deleting);

  return (
    <>
      <AddUnitModal
        open={addingUnder !== null}
        units={units}
        initialParentId={shownAddingUnder ?? topId}
        onCancel={onCancelAdd}
        onConfirm={onAdd}
      />

      <EditUnitModal
        open={editing !== null}
        units={units}
        unitId={shownEditing}
        onCancel={onCancelEdit}
        onConfirm={onEdit}
      />

      <MoveUnitModal
        open={moving !== null}
        units={units}
        unitId={shownMoving}
        expandedIds={expandedIds}
        onCancel={onCancelMove}
        onConfirm={onMove}
      />

      <DeleteUnitModal
        open={deleting !== null}
        units={units}
        unitId={shownDeleting}
        expandedIds={expandedIds}
        onCancel={onCancelDelete}
        onConfirm={onDelete}
      />
    </>
  );
};

export default UnitDialogs;
